import { useDrag, useDragLayer } from 'react-dnd';
import { WorkOrder } from '../../models/workOrder';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Icons } from '../../assets/icons';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import FormDateField, { DateFormat } from '../../components/hook-form/form-date.field.component';
import { useFormContext } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { useSaveData } from '../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';
import dayjs from 'dayjs';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useNavigate } from 'react-router-dom';
import { holidayManagementApiKeyStore } from '../../api/masters/holiday-management-api-key-store';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { useSnackbar } from '../../hooks/use-snackbar.hook';

interface InspectionsProps {
  inspections: WorkOrder[];
  inspectorCount: any;
  setSelectedInpsections: (values: any) => void;
  handleInspectionsFilterClick: () => void;
}

const CustomDragPreview = ({ count }: { count: number }) => {
  return (
    <div
      style={{
        padding: '8px 12px',
        backgroundColor: '#007bff',
        color: '#fff',
        borderRadius: '8px',
        fontWeight: 'bold',
        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
      }}
    >
      You’ve selected {count} {count === 1 ? 'item' : 'items'}
    </div>
  );
};

const CustomDragLayer = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item?.inspections) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        left: currentOffset.x,
        top: currentOffset.y,
        transform: 'translate(-50%, -50%)',
        zIndex: 1500,
      }}
    >
      <CustomDragPreview count={item.inspections.length} />
    </div>
  );
};

const InspectionsList = ({
  inspections,
  inspectorCount,
  passSelectedInspections,
  handleCalenderButtonClick,
}: {
  inspections: WorkOrder[];
  inspectorCount: any;
  passSelectedInspections: (value: any) => void;
  handleCalenderButtonClick: (woId: number, scheduleDate: any) => void;
}) => {
  const [selectedInspections, setSelectedInspections] = useState<WorkOrder[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    setSelectedInspections([]);
    setLastSelectedIndex(null);
  }, [inspections?.length]);

  const handleSelectedInspections = (values: any) => {
    if (typeof values === "function") {
      setSelectedInspections(prev => {
        const result = values(prev);
        passSelectedInspections(result);
        return result;
      });
    } else {
      setSelectedInspections(values);
      passSelectedInspections(values);
    }
  };

  if (!inspections || inspections.length === 0) {
    return <p style={{ textAlign: 'center' }}>No inspections available.</p>;
  }

  return (
    <div className="flex flex-col gap-4 p-4" style={{ width: '100%' }}>      
      <Grid container>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', padding: 0, marginBottom: '10px' }}>INSPECTIONS</h3>
            {inspectorCount && (
              <div style={{ textAlign: 'right', display: 'flex', marginLeft: 'auto' }}>
                <Typography variant="body2" style={{ paddingRight: '10px' }}>
                  Primary Inspection Count:
                  <div style={{ color: 'green', fontSize: '18px', display: 'inline' }}>
                    <b> {inspectorCount?.primaryInspectorInspectionCount ?? 0}</b>
                  </div>
                </Typography>
                <Typography variant="body2">
                  Secondary Inspection Count:
                  <div style={{ color: 'green', paddingLeft: "4px", fontSize: '18px', display: 'inline' }}>
                    <b>{inspectorCount?.secondaryInspectorInspectionCount ?? 0}</b>
                  </div>
                </Typography>
              </div>
            )}
            <div
        style={{
          margin: '3px 0 15px 0',
          padding: '10px 12px',
          backgroundColor: '#fff8e1',
          border: '1px solid #f0c36d',
          borderRadius: '4px',
          fontSize: '13px',
        }}
      >
        <strong>Note:</strong> Inspections scheduled for weekends/holidays will not
        be shown in Schedule Balancing. To view or make changes to these inspections,
        if existing, please use the Inspector Calendar dashboard.
      </div>
          </Grid>
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <CustomDragLayer />
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          {inspections.map((inspection, index) => (
            <DraggableInspection
              key={inspection?.workOrderID}
              inspection={inspection}
              index={index}
              inspections={inspections}
              handleCalenderButtonClick={handleCalenderButtonClick}
              selectedInspections={selectedInspections}
              setSelectedInspections={handleSelectedInspections}
              lastSelectedIndex={lastSelectedIndex}
              setLastSelectedIndex={setLastSelectedIndex}
            />
          ))}
        </Grid>
      </Grid>
    </div>
  );
};

const DraggableInspection = ({
  inspection,
  index,
  inspections,
  handleCalenderButtonClick,
  selectedInspections,
  setSelectedInspections,
  lastSelectedIndex,
  setLastSelectedIndex,
}: {
  inspection: WorkOrder;
  index: number;
  inspections: WorkOrder[];
  handleCalenderButtonClick: (woId: number, scheduleDate: any) => void;
  selectedInspections: WorkOrder[];
  setSelectedInspections: React.Dispatch<React.SetStateAction<WorkOrder[]>>;
  lastSelectedIndex: number | null;
  setLastSelectedIndex: React.Dispatch<React.SetStateAction<number | null>>;
}) => {
  const ishavePermission = useAuthorize([
    UserPermissionName?.MANAGE_SCHEDULEBALANCING,
  ]);

  const isSelected = selectedInspections.some(
    i => i?.workOrderID === inspection?.workOrderID
  );

  /* ---------------- WINDOWS STYLE SELECTION ---------------- */
  const handleSelection = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // Ignore clicks from buttons / popovers
    if (
      target.closest('.more-info-button') ||
      target.closest('.calendar-icon-button') ||
      target.closest('.MuiPopover-root') ||
      document.querySelector('.popOverInnererWrap')?.contains(target)
    ) {
      return;
    }

    const isCtrl = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;

    // SHIFT → range select
    if (isShift && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);

      setSelectedInspections(inspections?.slice(start, end + 1));
      return;
    }

    // CTRL → toggle selection
    if (isCtrl) {
      setSelectedInspections(prev => {
        const exists = prev.some(
          i => i?.workOrderID === inspection?.workOrderID
        );

        return exists
          ? prev.filter(i => i?.workOrderID !== inspection?.workOrderID)
          : [...prev, inspection];
      });

      setLastSelectedIndex(index);
      return;
    }

    // NORMAL CLICK → single select
    setSelectedInspections([inspection]);
    setLastSelectedIndex(index);
  };

  /* ---------------- DRAG SETUP ---------------- */
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'inspection',
      item: {
        inspections:
          selectedInspections?.length > 0
            ? selectedInspections
            : [inspection],
      },
      collect: monitor => ({
        isDragging: monitor?.isDragging(),
      }),
    }),
    [selectedInspections, ishavePermission]
  );

  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);


  const navigate = useNavigate();

  const [isWorkOrderHovered, setIsWorkOrderHovered] = useState(false);
  const handleClickWorkorderNumber = () => {
    // navigate(`/workorders?searchType=workorders&searchText=${inspection?.workOrderNumber}`);
    window.open(`/workorders?searchType=workorders&searchText=${inspection?.workOrderNumber}`, "_blank");
  };

  const [isJobsiteHovered, setIsJobsiteHovered] = useState(false);
  const handleClickJobsite = () => {
    window.open(`/units/profile/${inspection?.unitID}`, "_blank");
  };

  return (
    <div
      ref={ishavePermission ? drag : null}
      onClick={handleSelection}
      style={{
        // opacity: isDragging ? 0.5 : 1,
        // cursor: 'grab',
        // backgroundColor: '#f2f3f5',
        marginBottom: 5,
        padding: '5px 15px',
        backgroundColor: isSelected ? '#e0f0ff' : '#f2f3f5',
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        // padding: '10px',
        borderRadius: '8px',
        border: isSelected ? '2px solid #007bff' : '1px solid #ccc',
        userSelect: 'none',
      }}
      className="draggable-inspectionBlock bg-gray-100 bg-white shadow-md rounded-lg p-4 border border-gray-300 mb-4"
    >
      <Grid container>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Work Order:
          </small>
          <Typography
            className="font-semibold"
            style={{
              color: isWorkOrderHovered ? '#0000b5' : '#0000b5',
              textDecoration: isWorkOrderHovered ? 'underline' : 'none',
              cursor: 'pointer',
            }}
            onClick={handleClickWorkorderNumber}
            onMouseEnter={() => setIsWorkOrderHovered(true)}
            onMouseLeave={() => setIsWorkOrderHovered(false)}
          >
            {!inspection?.workOrderNumber ? '-' : inspection?.workOrderNumber}
          </Typography>
        </Grid>
        <Grid item xl={5} lg={5} md={5} sm={5} xs={5} style={{ maxWidth: 'auto' }}>
          <small className="gray" style={{ color: '#787878' }}>
            Service:
          </small>{' '}
          <Typography variant="body1"> {!inspection?.serviceName ? '-' : inspection?.serviceName}</Typography>
        </Grid>
        <Grid item xl={4} lg={4} md={4} sm={4} xs={4} style={{ maxWidth: 'auto' }}>
          <small className="gray" style={{ color: '#787878' }}>
            Municipality Code:
          </small>{' '}
          <Typography variant="body1"> {!inspection?.serviceMunicipalityCode ? '-' : inspection?.serviceMunicipalityCode}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3} style={{ maxWidth: 'auto' }}>
          <small className="gray" style={{ color: '#787878' }}>
            Permit Number:
          </small>{' '}
          <Typography variant="body1"> {!inspection?.permitNumber ? '-' : inspection?.permitNumber}</Typography>
        </Grid>

        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Scheduled Date:
          </small>
          <Typography>{!inspection?.scheduledDate ? '-' : inspection?.scheduledDate}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Primary Inspector:
          </small>
          <Typography>{!inspection?.inspectorName ? '-' : inspection?.inspectorName}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Secondary Inspector:
          </small>
          <Typography>{!inspection?.secondaryInspectorName ? '-' : inspection?.secondaryInspectorName}</Typography>
        </Grid>

        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Status:
          </small>
          <Typography>{!inspection?.workOrderStatus ? '-' : inspection?.workOrderStatus}</Typography>
        </Grid>

        <Grid item xl={3} lg={3} md={3} sm={3} xs={3} style={{ marginTop: 8 }}>
          <small className="gray" style={{ color: '#787878' }}>
            Address:
          </small>
          <Typography>{!inspection?.address ? '-' : inspection?.address}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Project Name:
          </small>
          <Typography>{!inspection?.projectName ? '-' : inspection?.projectName}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Lot #:
          </small>
          <Typography>{!inspection?.unitName ? '-' : inspection?.unitName}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            City:
          </small>
          <Typography>{!inspection?.city ? '-' : inspection?.city}</Typography>
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
          <small className="gray" style={{ color: '#787878' }}>
            Proposed Schedule Date:
          </small>
          <Typography variant="body1">
            <span style={{ color: inspection?.isPastProposedScheduledDate ? 'red' : 'black', marginRight: 10 }}>
              {inspection?.proposedScheduledDate}
            </span>
            {inspection?.workOrderStatus == "Ready" && (
              <IconButton
                className="calendar-icon-button"
                aria-label="move"
                onClick={() => handleCalenderButtonClick(inspection?.workOrderID, inspection?.proposedScheduledDate)}
                style={{ padding: 0, marginLeft: 0 }}
              >
                <Tooltip title={<Typography variant="body1">Change proposed schedule date</Typography>}>
                  <img style={{ width: 12 }} src={Icons.IconCalendar} alt="change proposed schedule date" />
                </Tooltip>
              </IconButton>
            )}
          </Typography>
        </Grid>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12} style={{ textAlign: 'right', display: 'flex', marginLeft: 'auto' }}>
          <Typography
            onClick={handlePopoverOpen}
            style={{
              cursor: 'pointer',
              color: '#007bff',

              display: 'inline-flex',
              alignItems: 'center',
              marginLeft: 'auto',
            }}
          >
            <button
              className="btn btn-sm btn-dark more-info-button"
              style={{ lineHeight: '1', cursor: 'pointer', marginLeft: 'auto' }}
            >
              More Info
            </button>
          </Typography>
        </Grid>
      </Grid>

      <Popover
        className="popOverOuterWrap"
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className="popOverInnererWrap">
          <Typography
            variant="body1"
            className="d-flex"
            style={{
              alignItems: 'center',
              justifyContent: 'left',
              borderBottom: '1px solid #ccc',
              paddingBottom: '5px',
            }}
          >
            <small>Customer: </small> {inspection?.customerName || ''}
          </Typography>
          <Typography
            variant="body1"
            className="d-flex"

          >
            <small>Jobsite: </small>
            <Typography variant='body1'
              onClick={handleClickJobsite}
              style={{
                color: isJobsiteHovered ? '#0000b5' : '#0000b5',
                textDecoration: isJobsiteHovered ? 'underline' : 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setIsJobsiteHovered(true)}
              onMouseLeave={() => setIsJobsiteHovered(false)}
            >
              {inspection?.jobsite || ''}
            </Typography>
          </Typography>

          <Typography variant="body1" className="d-flex">
            <small>Builder Job Number: </small> {inspection?.jobNumber || ''}
          </Typography>
          <Typography variant="body1" className="d-flex">
            <small>Office: </small> {inspection?.officeName || ''}
          </Typography>

          <Typography variant="body1" className="d-flex">
            <small>Project Code: </small> {inspection?.projectCode || ''}
          </Typography>
          <Typography variant="body1" className="d-flex">
            <small>Cut-off Time: </small> {inspection?.cutOffTime || ''}
          </Typography>
          <Typography variant="body1" className="d-flex">
            <small>Square Feet: </small> {inspection?.squareFeet || ''}
          </Typography>

          <Typography variant="body1" className="d-flex">
            <small>Zip: </small> {inspection?.zip || ''}
          </Typography>
          <Typography variant="body1" className="d-flex">
            <small>Skill Sets: </small> {inspection?.serviceSkillsets || ''}
          </Typography>
          <Typography variant="body1" className="d-flex">
            <small>Equipments: </small> {inspection?.serviceEquipments || ''}
          </Typography>
        </div>
      </Popover>
    </div>
  );
};

const Inspections = ({ inspections, inspectorCount, setSelectedInpsections, handleInspectionsFilterClick }: InspectionsProps) => {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentWorkOrderID, setCurrentWorkOrderID] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const { setValue, getValues, watch } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const [pendingRuleResponse, setPendingErrorRoles] = useState<any>();
  const [openRuleDialog, setRuleDialog] = useState<boolean>(false);

  const ishavePermission = useAuthorize([UserPermissionName?.OVERRIDE]);

  const { isLoading: isLoadingProposedSheduleDate, mutateAsync: updateProposedSheduleDate } = useSaveData<any>(
    workOrdersApiKeyStore.updateProposedScheduleDate()
  );

  const { isLoading: isPrerequisitesLoading, mutateAsync: mutatePrerequisitesAsync } = useSaveData<any>(workOrdersApiKeyStore.checkPrerequisitesBeforeReady());

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const validInspections = Array.isArray(inspections) ? inspections : [];

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedInspections = validInspections.slice(startIndex, startIndex + itemsPerPage);

  const handleCalenderButtonClick = (WoId: number, scheduleDate: any) => {
    console.log("proposedScheduledDate", scheduleDate)
    setValue("proposedSheduleDate", scheduleDate);
    setCurrentWorkOrderID(WoId);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setValue('proposedSheduleDate', undefined);
    setCurrentWorkOrderID(0);
  };

  const isHolidayOrWeekend = (scheduleDate: any, holidayDates: any) => {
    const selectedDate = dayjs(scheduleDate, "MM/DD/YYYY");

    // Check weekend
    const day = selectedDate.day(); // 0 = Sunday, 6 = Saturday
    const isWeekend = day === 0 || day === 6;

    // Check holiday
    const isHoliday = holidayDates?.some((holiday: any) =>
      dayjs(holiday.date, "MM/DD/YYYY").isSame(selectedDate, "day")
    );

    return {
      isWeekend,
      isHoliday,
      isInvalid: isWeekend || isHoliday,
    };
  };

  const handleSumbitConfirm = async () => {
    const result = isHolidayOrWeekend(dayjs(watch('proposedSheduleDate'))?.format('MM/DD/YYYY'), holidayDates);

    if (result?.isHoliday && shouldRestrictDateSelection) {
      showSnackbar(
        'The selected proposed schedule date falls on a holiday. Please choose a valid working day to proceed.',
        'error'
      );
      return;
    }
    if (result?.isWeekend && shouldRestrictDateSelection) {
      showSnackbar(
        'The selected proposed schedule date falls on a weekend. Please choose a valid working day to proceed.',
        'error'
      );
      return;
    }

    const rulePayload = {
      workOrderID: currentWorkOrderID,
      proposedScheduleDate: dayjs(watch('proposedSheduleDate'))?.format('MM/DD/YYYY')
    }
    const ruleResponse: any = await mutatePrerequisitesAsync({ data: rulePayload });

    const hasPendingRule = ruleResponse?.data?.pendingRules?.length;
    if (hasPendingRule > 0) {
      setPendingErrorRoles(ruleResponse?.data)
      setRuleDialog(true);
    }
    else {
      updateProposedSheduleDate({
        data: {
          workOrderID: currentWorkOrderID,
          proposedScheduledDate: watch('proposedSheduleDate'),
        },
      }).then((data: any) => {
        handleInspectionsFilterClick();
        closeDialog();
      });
    }
  };

  const closeValidationDialog = () => {
    setRuleDialog(false);
    setPendingErrorRoles(undefined);
  }

  const handleSumbitRuleConfirm = () => {
    closeValidationDialog();
    updateProposedSheduleDate({
      data: {
        workOrderID: currentWorkOrderID,
        proposedScheduledDate: watch('proposedSheduleDate'),
      },
    }).then((data: any) => {
      handleInspectionsFilterClick();
      closeDialog();
    });
  }

  const handleSelectedInspection = (values: any) => {
    setSelectedInpsections(values);
  }

  const [holidayDates, setHolidayDates] = useState<any[]>([]);
  const { isLoading: isHolidayLoading, data: holidayData } = useLoadData<ApiResponse<any[]>>(
    holidayManagementApiKeyStore.getHolidays());

  useEffect(() => {
    if (holidayData) {
      const datesResult = holidayData?.data
        .filter((item: any) => item.isActive)
        //?.map((item: any) => item.holidayDate);
        ?.map((item: any) => ({
          date: item.holidayDate,
          name: item.holidayName,
        }));
      setHolidayDates(datesResult);
    }
  }, [holidayData])

  //const isWeekendHolidaySchedulingOverridePermission = !useAuthorize([UserPermissionName?.WEEKEND_HOLIDAY_SCHEDULING_OVERRIDE]);

  const hasWeekendHolidayOverridePermission = useAuthorize([
    UserPermissionName.WEEKEND_HOLIDAY_SCHEDULING_OVERRIDE,
  ]);

  const shouldRestrictDateSelection = !hasWeekendHolidayOverridePermission;

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      <InspectionsList inspections={paginatedInspections} inspectorCount={inspectorCount} handleCalenderButtonClick={handleCalenderButtonClick} passSelectedInspections={handleSelectedInspection} />
      {openDialog && (
        <Dialog
          open={openDialog}
          fullWidth={true}
          maxWidth={'sm'}
        // onClose={onClosed}
        >
          <DialogTitle id="scroll-dialog-title">Proposed Schedule Date</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Tooltip title={<Typography variant="body1">Close</Typography>}>
              <CloseIcon />
            </Tooltip>
          </IconButton>

          {isLoadingProposedSheduleDate && <LoadingIndicator isLoading={isLoadingProposedSheduleDate} />}
          <DialogContent dividers className="popupDilog">
            <Grid container spacing={2}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormDateField
                  label="Proposed Schedule Date"
                  name="proposedSheduleDate"
                  dateLimits={{ minDateEnabled: true }}
                  format={DateFormat.SIMPLE_DATE}
                  //restrictDateSelection={isWeekendHolidaySchedulingOverridePermission}
                  //holidayDateList={isWeekendHolidaySchedulingOverridePermission ? holidayDates : []}
                  restrictDateSelection={shouldRestrictDateSelection}
                  holidayDateList={holidayDates}
                  enableDateConfirmation={hasWeekendHolidayOverridePermission}
                  isParentLoading={isHolidayLoading}
                  disabled={isHolidayLoading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
            <DialogActions className="spaceBetween">
              <Button
                type="button"
                variant='contained'
                // className={`btn btn-sm ${!getValues('proposedSheduleDate') ? '' : 'btn-primary'}`}
                style={{ width: '100px', marginLeft: 'auto' }}
                onClick={handleSumbitConfirm}
                disabled={!watch('proposedSheduleDate') || isLoadingProposedSheduleDate || isPrerequisitesLoading || isReadOnlyPermission}
              >
                Save
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}
      {validInspections.length > 0 && (
        <Grid
          padding={10}
          style={{
            padding: '20px 0px 15px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Pagination
            count={Math.ceil(validInspections.length / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
          <FormControl className="pagingperSelect" variant="outlined">
            <Select
              labelId="items-per-page-label"
              id="items-per-page-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            <span>items per page</span>
          </FormControl>
        </Grid>
      )}

      {openRuleDialog && (
        <Dialog
          open={openRuleDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                <h4>Please see the below validation</h4>
                <br></br>
                <ul>
                  {pendingRuleResponse?.pendingRules?.map((rules: any) => (
                    <div key={`${pendingRuleResponse?.workorderID}-${rules}`}>
                      {/* <li style={{ marginLeft: '25px' }}> */}
                      <span>{rules}</span>
                      {/* </li> */}
                      {rules.includes("pre-requisite") && pendingRuleResponse?.preReqWorkOrders?.length > 0 && (
                        <dl>
                          <dt>Pending Work Orders:</dt>
                          {pendingRuleResponse?.preReqWorkOrders?.map((data: any) => <dd key={data}>- {data}</dd>)}
                        </dl>
                      )}
                    </div>
                  ))}
                </ul>
              </div>
              <br></br>
              <div>
                {ishavePermission ? (
                  <h5>
                    You have permission to change the proposed schedule date without completing the validation.
                    Are you sure you want to
                    continue?
                  </h5>
                ) : (
                  <h5>You can not change the proposed schedule date without completing the validation.</h5>
                )}
              </div>
              {/* <LoadingIndicator isLoading={isLoading} /> */}
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeValidationDialog}
                autoFocus
              >
                Cancel
              </button>
              {ishavePermission && (
                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  type="button"
                  onClick={handleSumbitRuleConfirm}
                  // disabled={isLoading}
                  style={{ cursor: 'pointer' }}
                >
                  Confirm
                </button>
              )}
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default Inspections;
